import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useCallback, useState } from "react";

import type { DragonTileSelection } from "@/components/DragonTilePicker/types";

import { useUpdateFrame } from "@/api/dragon-tiles";
import { DragonTilePicker } from "@/components/DragonTilePicker/DragonTilePicker";
import { updateFramePayload } from "@/frames/service";

const EMPTY_SELECTION: DragonTileSelection = {
  dragonTileId: "",
  innerTileId: undefined,
  hasError: false,
};

interface AssignDragonTileModalProps {
  open: boolean;
  onClose: () => void;
  frameId: string;
  siteId: string;
}

/** Attaches a tile that lives outside any dragon tile to the chosen one. */
export function AssignDragonTileModal({
  open,
  onClose,
  frameId,
  siteId,
}: AssignDragonTileModalProps) {
  const [selection, setSelection] =
    useState<DragonTileSelection>(EMPTY_SELECTION);
  const { mutate: updateFrame, isPending } = useUpdateFrame();

  const handleSelectionChange = useCallback(
    (next: DragonTileSelection) => setSelection(next),
    [],
  );

  const handleClose = () => {
    if (isPending) return;
    setSelection(EMPTY_SELECTION);
    onClose();
  };

  const handleAssign = () => {
    if (!selection.innerTileId) return;
    updateFrame(
      {
        siteId,
        frameId,
        payload: updateFramePayload({ inner_tile: selection.innerTileId }),
        silent: true,
      },
      {
        onSuccess: () => {
          enqueueSnackbar("Tile assigned to dragon tile", {
            variant: "success",
          });
          setSelection(EMPTY_SELECTION);
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      // Portaled, but React still bubbles its events to the card's onClick.
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>Assign to Dragon Tile</DialogTitle>
      <DialogContent>
        <DragonTilePicker
          siteId={siteId}
          enabled={open}
          onSelectionChange={handleSelectionChange}
          fullWidth
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={isPending || !selection.innerTileId || selection.hasError}
        >
          {isPending ? "Assigning..." : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
